// Cell metadata + content loader for the canvas surface.
//
// The nav structure is locked from v6 RFC §"Locked structure":
//
//   📁 My Account            account cells (KYC, wallet, profile)
//   📁 Offerings              public per-restaurant cells
//   📁 My Investments         per-subscription cells
//
// Each cell is identified by name + optional contract / investment
// scope. The canvas route reads these from `?cell=&contract=&investment=`
// query params and asks this module to:
//   1. Resolve the cell metadata (sections, file path)
//   2. Fetch the markdown via /content/*
// If the file 404s, the renderer surfaces a friendly placeholder.
//
// Phase 5 simplifications (per INVESTOR_UX_2026-04-28.md §6.3):
//   - Hardcoded mirror of the metagraph cell list (codegen lands later)
//   - Single-restaurant offering list (sakura)
//   - Per-investment cells render placeholders until v6 carry-over (§5.6.3)
//
// Phase 6 swaps the static fetch for an agent-streamed render and adds
// metagraph-driven cell discovery; the call sites stay unchanged.

import { api } from '@rmint/shell';

// ─── Cell metadata ───────────────────────────────────────────────

export type CellScope = 'account' | 'offering' | 'investment';

export type CellMeta = {
  name: string;
  title: string;
  scope: CellScope;
  // Content path RESOLVER. The canvas route invokes this with the
  // current query params; the function returns one or more candidate
  // /content/* paths (tried in order; first 200 wins).
  resolvePaths: (ctx: { contractId?: string; investmentId?: string }) => string[];
};

// Mapping from short slugs (`?contract=sakura`) to repo content
// directories (`content/contracts/0x.../cells/`). Phase 6 reads this
// from a published-restaurants endpoint; Phase 5 mirrors it here so
// the canvas can route deep links without an extra fetch.
export const CONTRACT_DIRS: Record<string, string> = {
  sakura: 'contracts/0xe6CF06892E68A0f723538FA385AFD6C904f9d0C4',
};

// Account cells — global, not restaurant-scoped.
export const ACCOUNT_CELLS: CellMeta[] = [
  {
    name: 'kyc-submit',
    title: 'Submit KYC',
    scope: 'account',
    resolvePaths: () => ['wallets/global/cells/01-kyc-submit.md'],
  },
  {
    name: 'kyc-status',
    title: 'KYC Status',
    scope: 'account',
    resolvePaths: () => ['wallets/global/cells/02-kyc-status.md'],
  },
  {
    name: 'wallet',
    title: 'Wallet',
    scope: 'account',
    resolvePaths: () => ['wallets/global/cells/06-wallet.md'],
  },
  {
    name: 'holdings-summary',
    title: 'Holdings',
    scope: 'account',
    resolvePaths: () => ['wallets/global/cells/04-holdings-summary.md'],
  },
];

// Offering cells — applied per-restaurant. The resolver tries the
// per-restaurant override first, then falls back to the scaffold.
export const OFFERING_CELLS: CellMeta[] = [
  {
    name: 'metadata',
    title: 'About',
    scope: 'offering',
    resolvePaths: ({ contractId }) => offeringPaths(contractId, '01-metadata'),
  },
  {
    name: 'offering-overview',
    title: 'Overview',
    scope: 'offering',
    resolvePaths: ({ contractId }) => offeringPaths(contractId, '02-offering-overview'),
  },
  {
    name: 'financial-summary',
    title: 'Financials',
    scope: 'offering',
    resolvePaths: ({ contractId }) => offeringPaths(contractId, '03-financial-summary'),
  },
  {
    name: 'distribution-notice',
    title: 'Distributions',
    scope: 'offering',
    resolvePaths: ({ contractId }) => offeringPaths(contractId, '04-distribution-notice'),
  },
  {
    name: 'subscription-agreement',
    title: 'Agreement',
    scope: 'offering',
    resolvePaths: ({ contractId }) => offeringPaths(contractId, '05-subscription-agreement'),
  },
  {
    name: 'quarterly-update',
    title: 'Updates',
    scope: 'offering',
    resolvePaths: ({ contractId }) => offeringPaths(contractId, '06-quarterly-update'),
  },
];

// Per-investment cells — applied per-subscription. v7 doesn't yet
// have these template files (v6 carry-over deliverable for §5.6.3).
// The resolver returns a path that 404s today; the cell host renders
// a "template TBD" placeholder until §5.6.3 ports the templates.
export const INVESTMENT_CELLS: CellMeta[] = [
  {
    name: 'review',
    title: 'Review & Subscribe',
    scope: 'investment',
    resolvePaths: () => ['restaurants/_template/stakeholders/investors/cells/01-review.md'],
  },
  {
    name: 'payment',
    title: 'Payment',
    scope: 'investment',
    resolvePaths: () => ['restaurants/_template/stakeholders/investors/cells/02-payment.md'],
  },
  {
    name: 'subscription-sign',
    title: 'Sign Agreement',
    scope: 'investment',
    resolvePaths: () => ['restaurants/_template/stakeholders/investors/cells/03-subscription-sign.md'],
  },
  {
    name: 'custody-safe',
    title: 'Custody Safe',
    scope: 'investment',
    resolvePaths: () => ['restaurants/_template/stakeholders/investors/cells/04-custody-safe.md'],
  },
  {
    name: 'awaiting-finalization',
    title: 'Awaiting Finalization',
    scope: 'investment',
    resolvePaths: () => ['restaurants/_template/stakeholders/investors/cells/05-awaiting-finalization.md'],
  },
  {
    name: 'token-delivery',
    title: 'Token Delivery',
    scope: 'investment',
    resolvePaths: () => ['restaurants/_template/stakeholders/investors/cells/06-token-delivery.md'],
  },
];

function offeringPaths(contractId: string | undefined, basename: string): string[] {
  const dir = contractId ? CONTRACT_DIRS[contractId] : undefined;
  const paths: string[] = [];
  if (dir) paths.push(`${dir}/cells/${basename}.md`);
  paths.push(`notebook-scaffold/cells/${basename}.md`);
  return paths;
}

export const ALL_CELLS = [...ACCOUNT_CELLS, ...OFFERING_CELLS, ...INVESTMENT_CELLS];

export function findCell(name: string): CellMeta | undefined {
  return ALL_CELLS.find((c) => c.name === name);
}

// ─── Offering registry ───────────────────────────────────────────

export type Offering = {
  id: string;
  label: string;
  subtitle: string;
  contractAddress: string;
};

export const OFFERINGS: Offering[] = [
  {
    id: 'sakura',
    label: 'Sakura Ramen NC',
    subtitle: 'Charlotte, NC · open',
    contractAddress: '0xe6CF06892E68A0f723538FA385AFD6C904f9d0C4',
  },
];

export function findOffering(id: string | undefined): Offering | undefined {
  if (!id) return undefined;
  return OFFERINGS.find((o) => o.id === id);
}

// ─── Content loader ──────────────────────────────────────────────

export type CellContent = {
  // The path that resolved (the first candidate that returned 200).
  path: string;
  // Raw markdown body.
  body: string;
};

export type CellLoadResult =
  | { ok: true; content: CellContent }
  | { ok: false; tried: string[]; reason: 'not-found' | 'error'; message?: string };

export async function loadCell(
  meta: CellMeta,
  ctx: { contractId?: string; investmentId?: string },
): Promise<CellLoadResult> {
  const candidates = meta.resolvePaths(ctx);
  const tried: string[] = [];
  for (const candidate of candidates) {
    tried.push(candidate);
    try {
      const res = await api.get(`/content/${candidate}`, { responseType: 'text' });
      if (res.status === 200 && typeof res.data === 'string') {
        return { ok: true, content: { path: candidate, body: res.data } };
      }
      // 404 → fall through to next candidate. 4xx other than 404 →
      // surface as an error with the message so the cell host can
      // render a useful diagnostic.
      if (res.status !== 404) {
        return {
          ok: false,
          tried,
          reason: 'error',
          message: `${res.status} ${typeof res.data === 'string' ? res.data : ''}`,
        };
      }
    } catch (e) {
      return {
        ok: false,
        tried,
        reason: 'error',
        message: e instanceof Error ? e.message : String(e),
      };
    }
  }
  return { ok: false, tried, reason: 'not-found' };
}
